import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KEY = "oxo_cookie_consent";

export const openCookieSettings = () =>
  window.dispatchEvent(new Event("oxo:open-cookie-settings"));

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testid}
      className={`relative w-11 h-6 shrink-0 transition-colors ${
        disabled ? "bg-slate-300 cursor-not-allowed" : checked ? "bg-orange-600" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (!stored) {
      setShow(true);
    } else {
      try {
        const p = JSON.parse(stored);
        setPrefs({ analytics: !!p.analytics, marketing: !!p.marketing });
      } catch {
        /* legacy string value ("all"/"essential"): keep defaults */
      }
    }
    const openHandler = () => {
      const s = localStorage.getItem(KEY);
      if (s) {
        try {
          const p = JSON.parse(s);
          setPrefs({ analytics: !!p.analytics, marketing: !!p.marketing });
        } catch {
          /* ignore */
        }
      }
      setSettingsOpen(true);
    };
    window.addEventListener("oxo:open-cookie-settings", openHandler);
    return () => window.removeEventListener("oxo:open-cookie-settings", openHandler);
  }, []);

  const persist = (p) => {
    localStorage.setItem(KEY, JSON.stringify({ essential: true, ...p, ts: Date.now() }));
    window.dispatchEvent(new Event("oxo:consent-updated"));
    setPrefs(p);
    setShow(false);
    setSettingsOpen(false);
  };
  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const saveCustom = () => persist(prefs);

  const category = (title, desc, testid, node) => (
    <div className="flex items-start justify-between gap-4 border border-slate-200 p-4">
      <div>
        <div className="font-bold text-slate-900 text-sm">{title}</div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
      </div>
      {node}
    </div>
  );

  return (
    <>
      {show && (
        <div
          className="fixed bottom-0 inset-x-0 z-50 bg-slate-900 text-slate-200 border-t-2 border-orange-600"
          data-testid="cookie-consent"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Cookie className="w-6 h-6 text-orange-400 shrink-0 hidden sm:block" />
            <p className="text-sm flex-1 leading-relaxed">
              {t("cookie.text")}{" "}
              <Link
                to="/confidentialitate"
                className="text-orange-400 underline hover:text-orange-300"
                data-testid="cookie-learn"
              >
                {t("cookie.learn")}
              </Link>
            </p>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                onClick={() => setSettingsOpen(true)}
                className="border border-slate-600 hover:border-slate-400 text-slate-300 px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
                data-testid="cookie-settings"
              >
                {t("cookie.settings")}
              </button>
              <button
                onClick={rejectAll}
                className="border border-slate-600 hover:border-slate-400 text-slate-300 px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
                data-testid="cookie-reject"
              >
                {t("cookie.reject")}
              </button>
              <button
                onClick={acceptAll}
                className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
                data-testid="cookie-accept"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="cookie-settings-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-black">
              <Cookie className="w-5 h-5 text-orange-600" /> {t("cookie.modalTitle")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 leading-relaxed">{t("cookie.modalDesc")}</p>
          <div className="space-y-3 mt-2">
            {category(
              t("cookie.essentialTitle"),
              t("cookie.essentialDesc"),
              "cookie-cat-essential",
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400 shrink-0">
                <Lock className="w-3.5 h-3.5" /> {t("cookie.always")}
              </span>
            )}
            {category(
              t("cookie.analyticsTitle"),
              t("cookie.analyticsDesc"),
              "cookie-cat-analytics",
              <Toggle
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                testid="cookie-toggle-analytics"
              />
            )}
            {category(
              t("cookie.marketingTitle"),
              t("cookie.marketingDesc"),
              "cookie-cat-marketing",
              <Toggle
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                testid="cookie-toggle-marketing"
              />
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-end mt-3">
            <button
              onClick={rejectAll}
              className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="cookie-modal-reject"
            >
              {t("cookie.reject")}
            </button>
            <button
              onClick={saveCustom}
              className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-bold transition-colors"
              data-testid="cookie-modal-save"
            >
              {t("cookie.save")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
